import React, { useCallback } from 'react';
import Interactive, { Interaction } from '../drag-event-interactive';
import { HsvaColor, hsvaToHslaString } from '../utils/color-convert';
import { Pointer, PointerProps } from './Pointer';

export interface AlphaProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    prefixCls?: string;
    /** 选择器的宽度，默认 316px */
    width?: React.CSSProperties['width'];
    /** 选择器的高度，默认 16px */
    height?: React.CSSProperties['height'];
    /** hsva => `{ h: 0, s: 75, v: 82, a: 1 }` */
    hsva: HsvaColor;
    /** pointer 组件 */
    pointer?: ({ prefixCls, left }: PointerProps) => JSX.Element;
    radius?: React.CSSProperties['borderRadius'];
    background?: string;
    /** 背景属性 */
    bgProps?: React.HTMLAttributes<HTMLDivElement>;
    /** 小球属性 */
    innerProps?: React.HTMLAttributes<HTMLDivElement>;
    /** 水平方向或者垂直方向 */
    direction?: 'vertical' | 'horizontal';
    onChange?: (newAlpha: { a: number }, offset: Interaction) => void;
}

export const BACKGROUND_IMG =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==';

const Aplha = React.forwardRef<HTMLDivElement, AlphaProps>((props, ref) => {
    const {
        prefixCls = 'c-color-alpha',
        className,
        hsva,
        background,
        bgProps = {},
        innerProps = {},
        radius = 0,
        width,
        height = 16,
        direction = 'horizontal',
        style,
        onChange,
        pointer,
        ...other
    } = props;

    const handleChange = useCallback(
        (offset: Interaction) => {
            onChange && onChange({ ...hsva, a: direction === 'horizontal' ? offset.left : offset.top }, offset);
        },
        [hsva],
    );

    const colorTo = hsvaToHslaString(Object.assign({}, hsva, { a: 1 }));
    const innerBackground = `linear-gradient(to ${
        direction === 'horizontal' ? 'right' : 'bottom'
    }, rgba(244, 67, 54, 0) 0%, ${colorTo} 100%)`;
    const comProps: { left?: string; top?: string } = {};
    if (direction === 'horizontal') {
        comProps.left = `${hsva.a * 100}%`;
    } else {
        comProps.top = `${hsva.a * 100}%`;
    }

    return (
        <div
            {...other}
            className={[prefixCls, `${prefixCls}-${direction}`, className || ''].filter(Boolean).join(' ')}
            style={{
                borderRadius: radius,
                background: `url(${BACKGROUND_IMG}) left center`,
                backgroundSize: '8px',
                backgroundColor: '#fff',
                ...style,
                position: 'relative',
                ...{ width, height },
            }}
            ref={ref}
        >
            <div
                {...bgProps}
                style={{
                    inset: 0,
                    position: 'absolute',
                    background: background || innerBackground,
                    borderRadius: radius,
                    height,
                    minWidth: '100%',
                    ...bgProps.style,
                }}
            />
            <Interactive
                {...innerProps}
                style={{
                    ...innerProps.style,
                    inset: 0,
                    zIndex: 1,
                    position: 'absolute',
                }}
                onMove={handleChange}
                onDown={handleChange}
            >
                {React.createElement(pointer || Pointer, {
                    prefixCls,
                    hsva,
                    ...comProps,
                })}
            </Interactive>
        </div>
    );
});

Aplha.displayName = 'Aplha';

export default Aplha;
